import { Component, DestroyRef, computed, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import {
  VideoMessageState,
  VIDEO_ERROR_MESSAGE_KEYS,
} from '../../../../../../core/src/lib/interface/video-message-state.enum';

const CANCEL_DELAY_MS = 2000;

@Component({
  selector: 'inbox-video-state-bubble',
  imports: [CommonModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule, TranslateModule],
  templateUrl: './inbox-video-state-bubble.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './inbox-video-state-bubble.component.scss',
})
export class InboxVideoStateBubbleComponent {
  readonly videoState = input<VideoMessageState>();
  readonly posterUrl = input<string>();
  readonly videoUrl = input<string>();
  readonly errorMessageKey = input<string>();
  readonly retryable = input<boolean>(false);
  readonly stateMessageKeys = input<Partial<Record<VideoMessageState, string>>>();
  readonly cancelable = input<boolean>(true);

  readonly videoCancel = output<void>();
  readonly dismiss = output<void>();
  readonly retry = output<void>();

  readonly isProcessing = computed(() => this.videoState() === VideoMessageState.Processing);
  readonly isSending = computed(() => this.videoState() === VideoMessageState.Sending);
  readonly isSent = computed(() => this.videoState() === VideoMessageState.Sent);
  readonly isUploadInProgress = computed(() => this.videoState() === VideoMessageState.UploadInProgress);

  readonly isError = computed(() => {
    const state = this.videoState();
    return [
      VideoMessageState.Failed,
      VideoMessageState.Cancelled,
      VideoMessageState.UploadRejected,
      VideoMessageState.TranscodingFailed,
      VideoMessageState.OutputTooLarge,
      VideoMessageState.TwilioRejected,
    ].includes(state!);
  });

  readonly isUnknownState = computed(() =>
    !this.isUploadInProgress() && !this.isProcessing() && !this.isSending() && !this.isSent() && !this.isError(),
  );

  readonly showRetry = computed(() => this.retryable() || this.videoState() === VideoMessageState.TwilioRejected);
  readonly showDismiss = computed(() => this.isError());

  private readonly stateOverrideKey = computed(() => {
    const state = this.videoState();
    return state ? this.stateMessageKeys()?.[state] : undefined;
  });

  readonly processingMessageKey = computed(() => this.stateOverrideKey() || 'INBOX.VIDEO.STATUS.PROCESSING');

  readonly displayErrorMessageKey = computed(() => {
    const state = this.videoState();
    return (
      this.stateOverrideKey() ||
      this.errorMessageKey() ||
      (state && VIDEO_ERROR_MESSAGE_KEYS[state]) ||
      'INBOX.VIDEO.ERROR.UNKNOWN'
    );
  });

  readonly cancelVisible = signal(false);

  constructor() {
    const destroyRef = inject(DestroyRef);
    let timerId: ReturnType<typeof setTimeout> | undefined;

    effect(() => {
      clearTimeout(timerId);
      if (this.isProcessing() && this.cancelable()) {
        timerId = setTimeout(() => this.cancelVisible.set(true), CANCEL_DELAY_MS);
      } else {
        this.cancelVisible.set(false);
      }
    });

    destroyRef.onDestroy(() => clearTimeout(timerId));
  }
}
